import * as React from 'react';
import * as Chakra from '@chakra-ui/react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import { TopNavbar } from '@kitron/kitron-frontend';
import ServiceCard from './components/ServiceCard';
import { useGetServices } from './api/hooks';
import { loginRequest } from './authConfig';

import './navbarStyleOverride.css';

const useAuthToken = (scopes: string[], disableB2CAuth: boolean) => {
    const [token, setToken] = React.useState<string | undefined>(() => {
        const ssToken = sessionStorage.getItem('accessToken');
        if (ssToken) return ssToken;
        return undefined;
    });
    const [expiresAt, setExpiresAt] = React.useState<number | undefined>(() => {
        const ssExpiresAt = sessionStorage.getItem('expiresAt');
        if (ssExpiresAt) return +ssExpiresAt;
        return undefined;
    });

    const { instance, accounts, inProgress } = useMsal();
    const loginScopes = React.useRef(scopes);

    const performAuth = React.useCallback(() => {
        if (!disableB2CAuth) {
            const request = {
                scopes: loginRequest(loginScopes.current).scopes,
                account: accounts[0],
                refreshTokenExpirationOffsetSeconds: 5 * 60,
            };

            if (inProgress === InteractionStatus.None && !accounts.length) {
                instance.loginRedirect(request);
            }

            if (
                instance &&
                inProgress === InteractionStatus.None &&
                accounts.length
            ) {
                instance
                    .acquireTokenSilent(request)
                    .then((response) => {
                        sessionStorage.setItem(
                            'accessToken',
                            response.accessToken
                        );

                        setToken(response.accessToken);
                        if (response.expiresOn) {
                            const expires = +response.expiresOn;
                            setExpiresAt(expires); // Assuming expiresIn is in milli-seconds
                            sessionStorage.setItem(
                                'expiresAt',
                                expires.toString()
                            );
                        }
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.error(
                            'Error while fetching access token:',
                            error
                        );
                    });
            }
        }
    }, [accounts, disableB2CAuth, inProgress, instance]);

    React.useEffect(() => {
        if (!token) performAuth();
    }, [token, performAuth]);

    React.useEffect(() => {
        if (token && expiresAt) {
            const timeUntilExpiry = expiresAt - Date.now();
            setTimeout(performAuth, timeUntilExpiry - 4 * 60 * 1000);
        }
    }, [token, expiresAt, performAuth]);

    return { token, performAuth, account: accounts[0] };
};

const App: React.FC<{ scopes?: string[]; disableB2CAuth?: boolean }> = ({
    scopes = [],
    disableB2CAuth = false,
}) => {
    const { token, account } = useAuthToken(scopes, disableB2CAuth);
    const { data: services, isLoading: servicesLoading } = useGetServices({
        enabled: !!token || disableB2CAuth,
    });

    return !!token || disableB2CAuth ? (
        <>
            <Chakra.Box
                position='absolute'
                h='501px'
                w='100%'
                overflow='hidden'
            >
                <Chakra.Image
                    position='absolute'
                    h='100%'
                    w='100%'
                    fit='cover'
                    src='Electronics.png'
                />
                <Chakra.Box
                    position='absolute'
                    w='100%'
                    h='100%'
                    bgGradient='linear(to-b, #001D3A 100%, #001D3A 84%)'
                    opacity='78%'
                />
            </Chakra.Box>
            <TopNavbar
                titleContent={
                    <Chakra.Image
                        position='relative'
                        src='KitronLogo.png'
                        w='100px'
                        fit='contain'
                    />
                }
                user={{
                    userName: account.name,
                }}
            />
            <Chakra.Box position='absolute' w='100%' opacity='100%'>
                <Chakra.Center>
                    <Chakra.Heading color='Kitron.White' mb='20px' mt='40px'>
                        Kitron Business Portal
                    </Chakra.Heading>
                </Chakra.Center>
                <Chakra.Center>
                    <Chakra.Text color='Kitron.White' fontWeight={500}>
                        Extend your possibilities of work with Kitron
                    </Chakra.Text>
                </Chakra.Center>
            </Chakra.Box>
            <Chakra.Center position='absolute' top='383px' w='100%'>
                <Chakra.Box w='90%'>
                    <Chakra.Heading size='md' color='Kitron.White' mb={4}>
                        Services
                    </Chakra.Heading>
                    {servicesLoading ? (
                        <Chakra.Spinner />
                    ) : (
                        <Chakra.Grid templateColumns='repeat(3, 1fr)' gap={6}>
                            {services?.map((service) => {
                                return (
                                    <Chakra.GridItem key={service.name}>
                                        <ServiceCard service={service} />
                                    </Chakra.GridItem>
                                );
                            })}
                        </Chakra.Grid>
                    )}
                </Chakra.Box>
            </Chakra.Center>
        </>
    ) : (
        <Chakra.Flex
            display='flex'
            justifyContent='center'
            alignItems='center'
            width='100%'
            height='100vh'
            backgroundColor='#004990'
        >
            <Chakra.Flex flexFlow='column' alignItems='center'>
                <Chakra.Spinner
                    thickness='4px'
                    speed='0.65s'
                    emptyColor='gray.200'
                    color='blue.500'
                    size='lg'
                />
            </Chakra.Flex>
        </Chakra.Flex>
    );
};

export default App;
